<script lang="ts" setup>
import { onMounted, ref } from '#imports';
import { useService } from '@kaokei/use-vue-service';
import {
  getGoodsByMission,
  getMaliJiejinContent,
  getMaliJiejinSummary,
} from '~/api/oj/my';
import BadgePreviewModal from '~/components/BadgePreviewModal.vue';
import LoopShow from '~/components/LoopShow.vue';
import { UserService } from '~/services/user.service';
import type { HundredProblemList } from '~/types/auto/oj';
import { showErrorMsg } from '~/utils/utility';
import ModuleTitle from './ModuleTitle.vue';
import SeriesTrainingAnimation from './SeriesTrainingAnimation.vue';

type TabVO = Required<HundredProblemList> & {
  progress: number;
  lock: boolean; // 未解锁/已解锁
  received: boolean; // 已经领取
  receiveAnimation: boolean; // 点击领取按钮时显示动画
};

const userService = useService(UserService);

const visible = ref(true);
const badgePreviewModalVisible = ref(false);
const badgeVo = ref(); // 待领取的徽章
const activeKey = ref();

const list = ref<TabVO[]>([]);

function loadTabData(index: number) {
  return getMaliJiejinContent(index).then((res) => {
    const item = list.value.find((i) => i.missionId === res.missionId);
    if (item) {
      Object.assign(item, res);
      const completeCount = item.completeCount as number;
      const totalCount = item.totalCount as number;
      item.progress =
        totalCount > 0 ? Math.round((completeCount * 100) / totalCount) : 0;
      item.lock = totalCount > 0 && completeCount !== totalCount;
      item.received = item.rewardStatus === 1; // 已领取
    }
  });
}

function init() {
  getMaliJiejinSummary()
    .then((res) => {
      let activeIndex = 0;
      let activeId = 0;
      for (let i = 0; i < res.length; i++) {
        const item = res[i];
        if (!item.completed) {
          // 找到第一个未完成的tab
          activeIndex = item.index as number;
          activeId = item.missionId as number;
          break;
        }
      }

      list.value = res as TabVO[];
      if (list.value.length > 0) {
        if (activeId === 0) {
          // 所有tab全部已完成，默认选择第一个tab
          const first = res[0];
          activeIndex = first.index as number;
          activeId = first.missionId as number;
        }
        loadTabData(activeIndex);
        activeKey.value = activeId;
      } else {
        visible.value = false;
      }
    })
    .catch(() => {
      visible.value = false;
    });
}

function handleGetGoods(item: TabVO) {
  if (!item.lock && item.rewardStatus !== 1) {
    // 已解锁并且未领取
    getGoodsByMission(item.rewardId, item.missionId)
      .then((res) => {
        badgeVo.value = res;
        // 标记为已经领取状态
        item.rewardStatus = 1;
        // 这里先显示人物走动和跳动的动画
        // 等到这个小动画完成之后再执行showNextAnimation
        // showNextAnimation执行完成之后再显示badgePreviewModalVisible
        item.receiveAnimation = true;
      })
      .catch((e) => {
        showErrorMsg(e);
      });
  }
}
function handleAnimationFinish(item: TabVO, step = 1) {
  if (step === 1) {
    // 标记为已经领取状态，并且隐藏问号图标
    item.received = true;
    // 这里采用定时触发是因为黄光动画的结尾空白时间太长了
    window.setTimeout(() => (badgePreviewModalVisible.value = true), 3000);
  }
}

function handleTabIndexChange(id: number) {
  activeKey.value = id;
  const item: any = list.value.find((i) => i.missionId === id);
  if (item) {
    loadTabData(item.index);
  }
}

const loopShowDuration = 6000;

onMounted(() => {
  init();
});
</script>

<template>
  <div v-if="visible" class="rounded border border-lightblack bg-white p-3">
    <div v-if="list.length === 0">
      <div class="flex h-[38px] items-center justify-start pb-3">
        <ModuleTitle>
          <img
            src="~/assets/image/index/training/title@2x.png"
            alt="码力进阶"
            class="h-[18px]"
          />
        </ModuleTitle>
      </div>
      <a-skeleton />
    </div>
    <div v-else class="rounded">
      <a-tabs :active-key="activeKey">
        <a-tab-pane v-for="item in list" :key="item.missionId">
          <div v-if="item.hundredProblemDTOList" class="relative">
            <div class="training-list">
              <div
                v-if="item.userInfoList?.length"
                class="absolute left-5 right-2 top-2"
              >
                <div class="flex flex-nowrap justify-end gap-4">
                  <LoopShow
                    v-slot="{ item: user }"
                    :duration="loopShowDuration"
                    :list="item.userInfoList"
                  >
                    <div
                      :key="user.uid"
                      class="flex h-5 items-center justify-start rounded-full bg-[#00000066] pl-0.5 pr-2"
                    >
                      <img
                        v-if="user.avatar"
                        class="h-4 w-4 rounded-full"
                        :src="user.avatar"
                        alt="avatar"
                      />
                      <span class="mx-1 text-[10px] text-white">{{
                        user.nickname
                      }}</span>
                      <span class="text-[10px] text-white">已领取</span>
                    </div>
                  </LoopShow>
                </div>
              </div>

              <div class="grid grid-cols-4 justify-between gap-9">
                <NuxtLink
                  v-for="sub in item.hundredProblemDTOList"
                  :key="sub.trainingId"
                  v-track:newoj_homepage_codeexercise_show="{
                    training_id: sub.trainingId,
                  }"
                  v-track:newoj_homepage_codeexercise_click.click="{
                    training_id: sub.trainingId,
                  }"
                  class="training-box"
                  :class="{
                    doing: sub.status === 1,
                    finish: sub.status === 2,
                  }"
                  :to="{
                    name: 'oj_training_detail',
                    query: { tid: sub.trainingId },
                  }"
                  external
                >
                  <div
                    class="truncate px-1.5 text-xs text-colorText"
                    :title="sub.trainingTitle"
                  >
                    {{ sub.trainingTitle }}
                  </div>
                </NuxtLink>
              </div>

              <img
                v-if="!item.received"
                class="absolute bottom-32 right-[30px] h-8 w-8"
                src="~/assets/image/index/training/question@2x.png"
                alt="question"
              />

              <SeriesTrainingAnimation
                :receive="item.receiveAnimation"
                @animation-finish="handleAnimationFinish(item, $event)"
              />
            </div>

            <div
              class="absolute -top-[50px] right-0 flex h-[50px] items-center justify-center pl-5 text-sm leading-none"
            >
              <div class="ml-4 text-xs font-bold">
                <span
                  v-if="!userService.isLogin()"
                  v-login
                  class="cursor-pointer"
                  >请登录查看奖励进度</span
                >
                <span v-else-if="item.lock">完成挑战得谷子</span>
                <span v-else-if="item.received" class="text-font4"
                  >谷子已领取</span
                >
                <span v-else>谷子待领取</span>
              </div>

              <div
                v-if="userService.isLogin()"
                class="ml-2 flex items-center justify-start gap-1 text-xs font-bold"
              >
                <template v-if="item.lock">
                  <span>未解锁</span>
                  <span>{{ item.completeCount }}/{{ item.totalCount }}</span>
                </template>
                <template v-else-if="!item.received">
                  <a-button
                    class="!h-[26px] !px-4"
                    size="small"
                    type="primary"
                    @click="handleGetGoods(item)"
                    >领取</a-button
                  >
                </template>
              </div>

              <img
                v-if="item.lock"
                class="ml-3 h-6"
                src="~/assets/image/index/training/question@2x.png"
                alt="question"
              />

              <div v-if="item.received" class="ml-2 pr-2">
                <div class="tab-badge">
                  <img
                    v-track:newoj_homepage_codeexercise_goods_show="{
                      status_received: item.received ? 1 : 2,
                    }"
                    class="h-full w-full"
                    :src="item.rewardImage"
                    alt="badge"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <a-skeleton />
          </div>
        </a-tab-pane>

        <template #renderTabBar>
          <div class="flex items-center justify-start gap-x-2 pb-3">
            <ModuleTitle class="mr-1">
              <img
                src="~/assets/image/index/training/title@2x.png"
                alt="码力进阶"
                class="h-[18px]"
              />
            </ModuleTitle>

            <a
              v-for="item in list"
              :key="item.missionId"
              href="javascript:;"
              class="h-[26px] rounded-full border border-transparent px-[10px] py-0.5 text-sm font-bold !text-colorText"
              :class="{ 'active-tab': activeKey === item.missionId }"
              @click="handleTabIndexChange(item.missionId)"
            >
              {{ item.tabName }}</a
            >
          </div>
        </template>
      </a-tabs>
    </div>

    <BadgePreviewModal
      v-model:open="badgePreviewModalVisible"
      :data="badgeVo"
      :animate="true"
      :scene="2"
    />
  </div>
</template>

<style scoped>
.active-tab {
  @apply border-black;
}

.training-list {
  @apply relative z-10 rounded pb-24 pl-8 pr-20 pt-12;
  background: #e4f7ff url(~/assets/image/index/training/bg3@2x.png) no-repeat
    left bottom;
  background-size: contain;
}

.training-list .training-box {
  @apply relative z-10 flex h-12 w-[152px] items-center justify-center pb-3;
  background: #e4f7ff url(~/assets/image/index/training/box@2x.png) no-repeat
    center;
  background-size: contain;
}

.training-list .training-box.doing::after {
  content: ' ';
  @apply absolute -top-[14px] left-0 right-0 m-auto h-[18px] w-[55px];
  background: url(~/assets/image/index/training/doing@2x.png) no-repeat center;
  background-size: contain;
}
.training-list .training-box.finish::after {
  content: ' ';
  @apply absolute -top-[14px] left-0 right-0 m-auto h-[18px] w-[55px];
  background: url(~/assets/image/index/training/finish@2x.png) no-repeat center;
  background-size: contain;
}

.tab-badge {
  position: relative;
  width: 81px;
  height: 81px;
  transform: rotate(15deg);
  user-select: none;
}
</style>
